/**
 * Two reads a host needs to run a caret-anchored list (the @ and / pickers) on
 * the rich editor, which is a contenteditable and not a textarea: the words
 * before the caret, to find a trigger, and the caret's rect, to place the
 * list. Both read the live Selection at CALL time and write nothing into the
 * editor, so its serializer never sees them.
 */
import { serializeComposer } from "./serialize";

/**
 * The words before the caret, serialized the way the editor SENDS them (lists
 * as "1. " and "- " lines), so an offset into this string is an offset into
 * the words a host already holds. Null when there is no selection or the caret
 * is outside the editor. The serializer drops trailing empty lines, so the
 * caret's OWN empty line is put back: a caret at the start of a line reads as
 * past the newline, and a trigger on the line above is not the token it sits
 * in. One softness left, accepted: under SEVERAL empty lines only one newline
 * is kept, so the length is short there. On the LAST, empty line this string
 * is longer than the words a host holds, and that is where the scan gives up:
 * `useMentionSkillPickers`'s triggerStart returns -1 for a caret past the
 * words, so no list opens on a line the serializer trimmed and no pick ever
 * splices at such an offset.
 */
export function textBeforeCaret(root: HTMLElement): string | null {
  const sel = typeof window === "undefined" ? null : window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return null;
  const before = document.createRange();
  before.selectNodeContents(root);
  before.setEnd(range.endContainer, range.endOffset);
  const holder = document.createElement("div");
  holder.appendChild(before.cloneContents());
  const text = serializeComposer(holder);
  const last = holder.lastElementChild;
  // The serializer trims trailing empties; the caret's own line must survive,
  // or an offset into this string is not an offset into the editor's text.
  return last && (last.textContent ?? "") === "" && text !== ""
    ? `${text}\n`
    : text;
}

/**
 * The caret's rect, shaped the way the dropdowns place themselves against a
 * textarea: the editor's left and width, the caret's top and height. An empty
 * line gives the caret a zero rect, and some engines (jsdom) give a Range no
 * rect at all: the editor's own box stands in, so a list never opens at 0,0.
 */
export function caretRectOf(root: HTMLElement): DOMRect {
  const box = root.getBoundingClientRect();
  const sel = typeof window === "undefined" ? null : window.getSelection();
  if (sel && sel.rangeCount > 0) {
    const range = sel.getRangeAt(0);
    if (
      root.contains(range.endContainer) &&
      typeof range.getBoundingClientRect === "function"
    ) {
      const caret = range.cloneRange();
      caret.collapse(false);
      const r = caret.getBoundingClientRect();
      if (r && r.height > 0)
        return new DOMRect(box.left, r.top, box.width, r.height);
    }
  }
  return new DOMRect(box.left, box.top, box.width, box.height);
}
